/**
 * Browser-safe UUID v4 generator.
 *
 * `crypto.randomUUID()` is only exposed in **secure contexts** (HTTPS or
 * `localhost`). A pilot served over plain HTTP from a remote host is NOT a
 * secure context, so `crypto.randomUUID` is `undefined` there even though
 * `crypto.getRandomValues` remains available — this is why bulk import worked
 * on localhost but threw `crypto.randomUUID is not a function` on deployment.
 *
 * Resolution order:
 *   1. `crypto.randomUUID()` when available (fast, native, secure contexts).
 *   2. RFC-4122 v4 built from `crypto.getRandomValues` (available in every
 *      modern browser regardless of secure-context status).
 *   3. `Math.random()` fallback for ancient runtimes without Web Crypto.
 */
export function generateUuid(): string {
  const cryptoObj: Crypto | undefined =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;

  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }

  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    // Per RFC 4122 §4.4: set version (4) and variant (10xx) bits.
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return (
      hex.slice(0, 4).join('') +
      '-' +
      hex.slice(4, 6).join('') +
      '-' +
      hex.slice(6, 8).join('') +
      '-' +
      hex.slice(8, 10).join('') +
      '-' +
      hex.slice(10, 16).join('')
    );
  }

  // Last-resort fallback (non-cryptographic). Only reached on runtimes with no
  // Web Crypto at all — acceptable for a client-side idempotency key.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
